"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, Lock, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";
import { useT } from "@/lib/i18n";

import { useTenant } from "@/components/TenantProvider";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const { tenant } = useTenant();
  const isIpung = tenant.businessType === "personal";
  const primary = isIpung ? "#0054A6" : (tenant.settings.design?.primaryColor || tenant.themeColor);
  const FONT = isIpung ? "'JetBrains Mono', ui-monospace, monospace" : undefined;
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const box = isIpung ? "min-h-screen flex items-center justify-center px-4 pt-20 pb-24 bg-[#f1f1f2] dark:bg-[#141416]"
    : "min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg px-4 pt-20 pb-24";
  const card = isIpung ? "border border-[#dadada] dark:border-gray-700 bg-white dark:bg-[#0e0e10] p-8"
    : "rounded-[28px] p-8 bg-light-card dark:bg-dark-card border border-dark-text-secondary/5 dark:border-white/5 shadow-sm";
  const input = isIpung
    ? "w-full pl-11 pr-4 py-3 text-sm font-light bg-white dark:bg-[#0e0e10] border border-[#dadada] dark:border-gray-700 text-[#0e0e10] dark:text-white placeholder:text-gray-400 focus:outline-none focus:border-[#0054A6]"
    : "w-full pl-11 pr-4 py-3 rounded-2xl bg-light-bg dark:bg-dark-bg border border-dark-text-secondary/10 dark:border-white/10 text-light-text-primary dark:text-dark-text-primary placeholder:text-dark-text-secondary/40 focus:outline-none focus:border-gold/40";
  const label = isIpung ? "block text-sm font-bold mb-2 text-[#0e0e10] dark:text-white"
    : "block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2";
  const btnValid = name && email && phone && password && confirm;
  const btn = isIpung
    ? cn("w-full py-3.5 font-bold text-sm transition-all active:scale-[0.98]", btnValid ? "bg-[#0054A6] text-white hover:bg-[#004090] cursor-pointer" : "bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed")
    : cn("w-full py-3.5 rounded-2xl font-semibold text-base transition-all active:scale-[0.98]", btnValid ? "bg-gold text-black hover:bg-gold-light shadow-[0_8px_32px_rgba(117,53,14,0.2)]" : "bg-dark-text-secondary/10 text-dark-text-secondary/40 cursor-not-allowed");

  const valid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError("");
    if (!name.trim() || !email.trim() || !phone.trim() || !password || !confirm) return setError(t("register.name") + " & semua field wajib diisi");
    if (!valid(email)) return setError("Format email tidak valid");
    if (password.length < 6) return setError("Password minimal 6 karakter");
    if (password !== confirm) return setError("Password tidak cocok");
    setLoading(true);

    const r = await register(name.trim(), email.trim(), phone.trim(), password);
    if (!r.success) {
      setLoading(false);
      setError(r.error || "Registrasi gagal");
      return;
    }

    // Auto-login after register — wait for register state to settle, then login
    const loginR = await login(email.trim(), password);
    setLoading(false);

    if (loginR.success) {
      navigate("/dashboard", { replace: true });
    } else {
      // Registered OK but login failed — still redirect
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className={box} style={{ fontFamily: FONT }}>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/">
            {tenant.logoUrl ? (
              <>
                <img src={tenant.logoUrl} alt={tenant.name} className="h-10 mx-auto dark:hidden" />
                <img src={tenant.logoUrl.replace("-hitam", "-putih") || tenant.logoUrl.replace(".png", "-putih.png")} alt={tenant.name} className="h-10 mx-auto hidden dark:block" />
              </>
            ) : (
              <span className="text-2xl font-extrabold" style={{ color: primary, fontFamily: FONT }}>{tenant.name}</span>
            )}
          </Link>
          <p className="mt-3 text-light-text-secondary dark:text-dark-text-secondary text-sm" style={{ fontFamily: FONT }}>{t("register.title")}</p>
        </div>

        <div className={card}>
          {error && <div className="mb-4 p-3 border border-red-500/20 text-red-500 text-sm font-light" style={{ backgroundColor: "rgba(239,68,68,0.05)" }}>{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            {[{ icon: User, val: name, set: setName, ph: t("register.name"), type: "text" },
              { icon: Mail, val: email, set: setEmail, ph: t("register.email"), type: "email" },
              { icon: Phone, val: phone, set: setPhone, ph: t("register.phone"), type: "tel" }].map((f) => (
              <div key={f.ph}>
                <label className={label} style={{ fontFamily: FONT }}>{f.ph}</label>
                <div className="relative">
                  <f.icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1} />
                  <input type={f.type} value={f.val} onChange={(e) => f.set(e.target.value)} placeholder={f.ph}
                    className={input} style={{ fontFamily: FONT }} />
                </div>
              </div>
            ))}
            <div>
              <label className={label} style={{ fontFamily: FONT }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1} />
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimal 6 karakter"
                  className={cn(input, "pr-12")} style={{ fontFamily: FONT }} />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#0e0e10] dark:hover:text-white">
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1} /> : <Eye className="w-4 h-4" strokeWidth={1} />}
                </button>
              </div>
            </div>
            <div>
              <label className={label} style={{ fontFamily: FONT }}>Konfirmasi Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1} />
                <input type={showCf ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ulangi password"
                  className={cn(input, "pr-12")} style={{ fontFamily: FONT }} />
                <button type="button" onClick={() => setShowCf(!showCf)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#0e0e10] dark:hover:text-white">
                  {showCf ? <EyeOff className="w-4 h-4" strokeWidth={1} /> : <Eye className="w-4 h-4" strokeWidth={1} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading || !btnValid} className={btn} style={{ fontFamily: FONT }}>
              {loading ? <span className={cn("w-5 h-5 border-2 rounded-full animate-spin", isIpung ? "border-white/30 border-t-white" : "border-black/30 border-t-black")} /> : "Daftar"}
            </button>
          </form>

          <p className={cn("text-center text-sm mt-6", isIpung ? "text-gray-400" : "text-light-text-secondary dark:text-dark-text-secondary")} style={{ fontFamily: FONT }}>
            Sudah punya akun?{" "}<Link to="/login" className="font-bold transition-colors" style={{ color: primary }}>Masuk</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
